/**
 * Dispatch — Challans (delivery notes) and Weighbridge.
 * New for the RMC conversion. Backend: routers/dispatch.py.
 * Creating a Challan also creates its Production Entry (see Production.jsx's
 * log) — concrete can't be stockpiled, so one truckload = one batch.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, Truck, LayoutDashboard, ChevronRight, Timer,
  PenLine, MapPin, FileDown, Printer, Route,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { inrCompact, formatISTDateTime, statusColor } from '../lib/format';

const TABS = ['Dashboard', 'In Transit', 'Challans', 'Weighbridge'];

export default function Dispatch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Challans');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Challans' ? {} : { tab: t }); };
  return (
    <div data-testid="dispatch-page">
      <PageHeader
        overline="Sales & Dispatch"
        title="Dispatch"
        subtitle="Delivery challans per truckload, and weighbridge gate readings."
      />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
              data-testid={`dispatch-tab-${t.toLowerCase()}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Dashboard' && <DashboardTab />}
          {tab === 'In Transit' && <InTransitTab />}
          {tab === 'Challans' && <ChallansTab />}
          {tab === 'Weighbridge' && <WeighbridgeTab />}
        </div>
      </div>
    </div>
  );
}

function KTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="kpi-overline">{label}</div>
        {Icon && <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}><Icon size={13} /></div>}
      </div>
      <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ── Pour-window chip ─────────────────────────────────────────────────────────
function PourChip({ pw }) {
  if (!pw) return null;
  if (pw.minutes_used != null) {
    // delivered — frozen record
    return (
      <span className={`tag ${pw.breached ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
        title="Time from batching to delivery">
        {pw.minutes_used} min {pw.breached ? '· late' : ''}
      </span>
    );
  }
  const cls = pw.state === 'breached' ? 'bg-red-100 text-red-700'
    : pw.state === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-[#1F7A6E]';
  const label = pw.minutes_remaining < 0
    ? `${-pw.minutes_remaining} min over`
    : `${pw.minutes_remaining} min left`;
  return <span className={`tag ${cls}`} title="Pour window countdown">{label}</span>;
}

// ── In-Transit board ─────────────────────────────────────────────────────────
function InTransitTab() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => apiGet('/dispatch/in-transit').then(setData).catch(() => toast.error('Failed to load in-transit board'));
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // countdown refresh
    return () => clearInterval(t);
  }, []);
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="kpi-overline">Trucks out · {data.total}</div>
        {data.warning > 0 && <span className="tag bg-amber-100 text-amber-700">{data.warning} closing on pour window</span>}
        {data.breached > 0 && <span className="tag bg-red-100 text-red-700">{data.breached} over the window</span>}
        <span className="text-[10px] text-slate-400 ml-auto">Pour window: {data.pour_window_minutes} min from batching · auto-refreshes</span>
      </div>
      {data.items.length === 0 && (
        <div className="text-slate-400 py-10 text-center text-sm">No trucks in transit — every dispatched load has been delivered.</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.items.map((c) => {
          const pw = c.pour_window || {};
          const border = pw.state === 'breached' ? 'border-red-300 bg-red-50/40'
            : pw.state === 'warning' ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200';
          return (
            <button key={c.id} onClick={() => setDetail(c)}
              className={`text-left border ${border} rounded-sm p-3 hover:shadow-sm transition-shadow`}
              data-testid={`transit-card-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-xs text-[#1F7A6E] font-semibold">{c.challan_no}</span>
                <PourChip pw={pw} />
              </div>
              <div className="font-display font-bold text-sm">{c.grade} · {c.qty_m3} m³</div>
              <div className="text-xs text-slate-600 truncate flex items-center gap-1">
                {c.diversion_history?.length > 0 && <Route size={11} className="text-amber-600 shrink-0" />}
                {c.party_name}{c.jobsite ? ` — ${c.jobsite}` : ''}
              </div>
              <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                <Truck size={11} /> <span className="font-mono">{c.vehicle_no}</span>
                <Timer size={11} className="ml-2" /> out {formatISTDateTime(c.dispatch_time)}
              </div>
            </button>
          );
        })}
      </div>
      {detail && <ChallanDetailModal challan={detail} onClose={() => setDetail(null)} onUpdated={() => { setDetail(null); load(); }} />}
    </div>
  );
}

// ── Signature pad (pointer-events canvas) ────────────────────────────────────
function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = '#0f172a'; ctx.lineWidth = 2; ctx.lineCap = 'round';
  }, []);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [(e.clientX - r.left) * (ref.current.width / r.width), (e.clientY - r.top) * (ref.current.height / r.height)];
  };
  const down = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext('2d'); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext('2d'); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); dirty.current = true; };
  const up = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL('image/png')); drawing.current = false; };
  const clear = () => { const cv = ref.current; const ctx = cv.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cv.width, cv.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={560} height={160}
        className="w-full border border-slate-300 rounded-sm touch-none bg-white"
        style={{ touchAction: 'none' }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        data-testid="signature-pad" />
      <button type="button" onClick={clear} className="text-[11px] text-slate-400 hover:text-slate-700 mt-1">Clear signature</button>
    </div>
  );
}

// ── Deliver (ePOD) modal ─────────────────────────────────────────────────────
function DeliverModal({ challan, onClose, onDone }) {
  const [form, setForm] = useState({ receiver_name: '', receiver_phone: '', water_added_l: '', qty_rejected_m3: '', remarks: '' });
  const [signature, setSignature] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [gps, setGps] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => {}, { timeout: 8000 },
      );
    }
  }, []);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error('Photo too large (max 3 MB)');
    const rd = new FileReader();
    rd.onload = () => setPhoto(rd.result);
    rd.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.receiver_name.trim()) return toast.error('Receiver name is required');
    setSaving(true);
    try {
      const res = await apiPatch(`/dispatch/challans/${challan.id}/deliver`, {
        receiver_name: form.receiver_name.trim(),
        receiver_phone: form.receiver_phone || null,
        signature_base64: signature,
        photo_base64: photo,
        delivery_lat: gps?.lat ?? null, delivery_lng: gps?.lng ?? null,
        water_added_l: form.water_added_l ? Number(form.water_added_l) : null,
        qty_rejected_m3: Number(form.qty_rejected_m3 || 0),
        remarks: form.remarks || null,
      });
      toast.success(res.pour_window_breached
        ? `Delivered — ${res.delivery_minutes_after_batch} min after batching (over the pour window)`
        : `Delivered — ${res.delivery_minutes_after_batch} min after batching`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record delivery'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Delivery Ticket — ${challan.challan_no}`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="text-xs text-slate-500">{challan.grade} · {challan.qty_m3} m³ → {challan.party_name}{challan.jobsite ? ` (${challan.jobsite})` : ''}</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Receiver Name *"><input value={form.receiver_name} onChange={set('receiver_name')} className="input" autoFocus /></Field>
          <Field label="Receiver Phone"><input value={form.receiver_phone} onChange={set('receiver_phone')} className="input" /></Field>
        </div>
        <Field label="Receiver Signature"><SignaturePad onChange={setSignature} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Site Photo (optional)">
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="text-xs" />
            {photo && <img src={photo} alt="site" className="mt-1 h-16 rounded-sm border border-slate-200 object-cover" />}
          </Field>
          <div className="text-xs text-slate-500 flex items-end gap-1 pb-1">
            <MapPin size={12} />
            {gps ? `GPS captured (${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)})` : 'GPS unavailable — recorded without location'}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Water Added at Site (L)"><input type="number" value={form.water_added_l} onChange={set('water_added_l')} className="input" placeholder="0" /></Field>
          <Field label="Qty Rejected (m³)"><input type="number" value={form.qty_rejected_m3} onChange={set('qty_rejected_m3')} className="input" placeholder="0" /></Field>
        </div>
        <Field label="Remarks"><input value={form.remarks} onChange={set('remarks')} className="input" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <><PenLine size={13} className="mr-1" /> Confirm Delivery</>}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet('/dispatch/summary').then(setData).catch(() => toast.error('Failed to load dashboard')); }, []);
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KTile label="Challans Today" value={data.today_challan_count} icon={Truck} accent="text-[#1F7A6E] bg-teal-50" />
        <KTile label="Dispatched Today" value={`${data.today_qty_m3} m³`} icon={LayoutDashboard} />
        <KTile label="Revenue Today" value={inrCompact(data.today_revenue)} icon={LayoutDashboard} accent="text-emerald-700 bg-emerald-50" />
        <KTile label="Vehicles Out Today" value={data.vehicles_dispatched} icon={Truck} />
      </div>
      {data.today_by_grade.length > 0 && (
        <div>
          <div className="kpi-overline mb-2">Today's Dispatch by Grade</div>
          <div className="flex flex-wrap gap-2">
            {data.today_by_grade.map((g) => (
              <div key={g.grade} className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm">
                <span className="font-display font-bold">{g.grade}</span> — {g.qty_m3} m³
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Challans ─────────────────────────────────────────────────────────────────
function ChallansTab() {
  const [items, setItems] = useState([]);
  const [mixDesigns, setMixDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([apiGet('/dispatch/challans'), apiGet('/production/mix-designs')])
      .then(([ch, mx]) => { setItems(ch.items || []); setMixDesigns(mx.items || []); })
      .catch(() => toast.error('Failed to load challans'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Challans · {items.length}</div>
        <button onClick={() => setShowNew(true)}
          disabled={mixDesigns.length === 0}
          title={mixDesigns.length === 0 ? 'Create a Mix Design first (Plant Production → Mix Designs)' : 'Challans draw down an open Sales Order'}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] disabled:opacity-40 text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="challan-new-btn">
          <Plus size={13} /> New Challan
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Challan No</th><th>Date</th><th>Party</th><th>Grade</th>
            <th className="text-right">Qty (m³)</th><th>Vehicle</th><th>Status</th>
            <th className="text-right">Amount</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No challans yet — raise your first one above.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetail(c)} data-testid={`challan-row-${c.id}`}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{c.challan_no}</td>
                <td className="text-xs">{formatISTDateTime(c.date)}</td>
                <td>{c.party_name}</td>
                <td className="font-display font-bold">{c.grade}</td>
                <td className="text-right tabular-nums">{c.qty_m3}</td>
                <td className="font-mono text-xs">{c.vehicle_no}</td>
                <td><span className={`tag ${statusColor(c.status)}`}>{c.status}</span> <PourChip pw={c.pour_window} /></td>
                <td className="text-right tabular-nums font-display font-bold">{inrCompact(c.total_amount)}</td>
                <td className="text-right"><ChevronRight size={14} className="text-slate-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <NewChallanModal mixDesigns={mixDesigns} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detail && <ChallanDetailModal challan={detail} onClose={() => setDetail(null)} onUpdated={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function NewChallanModal({ mixDesigns, onClose, onSaved }) {
  const [orderLines, setOrderLines] = useState([]);
  const [form, setForm] = useState({
    line_key: '', mix_design_id: '', qty_m3: '', delivery_address: '',
    vehicle_no: '', driver_name: '', plant: '', batch_no: '', slump: '',
    pump_used: false, pump_gang: '', rate_override: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.type === 'checkbox' ? e.target.checked : e.target.value });

  useEffect(() => { apiGet('/commercial/orders/open-lines').then((r) => setOrderLines(r.items || [])).catch(() => {}); }, []);
  const line = orderLines.find((l) => `${l.order_id}|${l.seq}` === form.line_key);

  // When an order line is picked, auto-select the matching mix design by grade.
  useEffect(() => {
    if (!line) return;
    const mx = mixDesigns.find((m) => m.grade === line.grade);
    setForm((f) => ({ ...f, mix_design_id: mx ? mx.id : '', pump_used: line.pumping === 'With Pump' }));
  }, [form.line_key]); // eslint-disable-line react-hooks/exhaustive-deps

  const effRate = form.rate_override !== '' ? Number(form.rate_override) : (line ? line.rate : 0);
  const gst = line ? line.gst_pct : 18;

  const save = async () => {
    if (!line) return toast.error('Pick an order line to dispatch against');
    if (!form.mix_design_id) return toast.error('No mix design matches this grade — create one in Plant Production');
    if (!form.qty_m3 || Number(form.qty_m3) <= 0) return toast.error('Enter a valid quantity');
    if (Number(form.qty_m3) > line.pending_qty + 0.001) return toast.error(`Only ${line.pending_qty} m³ pending on this order line`);
    if (!form.vehicle_no.trim()) return toast.error('Vehicle number is required');
    setSaving(true);
    try {
      const ch = await apiPost('/dispatch/challans', {
        order_id: line.order_id, order_item_seq: line.seq,
        mix_design_id: form.mix_design_id, qty_m3: Number(form.qty_m3),
        delivery_address: form.delivery_address || null,
        vehicle_no: form.vehicle_no, driver_name: form.driver_name || null,
        plant: form.plant || null, batch_no: form.batch_no || null,
        slump: form.slump ? Number(form.slump) : null,
        pump_used: form.pump_used, pump_gang: form.pump_gang || null,
        rate_per_m3: form.rate_override !== '' ? Number(form.rate_override) : null,
      });
      toast.success(`${ch.challan_no} raised — ${ch.qty_m3} m³ ${ch.grade} against ${ch.order_no}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to raise challan'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="New Dispatch Challan" onClose={onClose} wide>
      <div className="space-y-3">
        <Field label="Sales Order Line * (mandatory — challan draws down the order)">
          <select value={form.line_key} onChange={set('line_key')} className="input" autoFocus data-testid="challan-order-line">
            <option value="">Select an order + grade…</option>
            {orderLines.map((l) => <option key={`${l.order_id}|${l.seq}`} value={`${l.order_id}|${l.seq}`}>{l.order_no} · {l.party_name} · {l.grade} ({l.pumping}) · {l.pending_qty} m³ pending @ ₹{l.rate}</option>)}
          </select>
          {orderLines.length === 0 && <div className="text-xs text-amber-600 mt-1">No open orders with pending quantity — create a Sales Order first (Sales & Orders → New Order).</div>}
        </Field>

        {line && (
          <div className="bg-teal-50 border border-teal-200 rounded-sm p-2 text-xs text-slate-700">
            <b>{line.party_name}</b> · {line.grade} {line.pumping} · order {line.order_no} · <b>{line.pending_qty} m³ pending</b> at ₹{line.rate}/m³{line.job_site ? ` · site: ${line.job_site}` : ''}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Grade / Mix Design (from order)">
            <select value={form.mix_design_id} onChange={set('mix_design_id')} className="input">
              <option value="">{line ? 'No mix design for this grade' : 'Pick an order line first'}</option>
              {mixDesigns.map((m) => <option key={m.id} value={m.id}>{m.grade} — {m.name}</option>)}
            </select>
          </Field>
          <Field label={`Quantity (m³)${line ? ` · max ${line.pending_qty}` : ''}`}><input type="number" value={form.qty_m3} onChange={set('qty_m3')} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vehicle No"><input value={form.vehicle_no} onChange={set('vehicle_no')} className="input" placeholder="MH14EM8861" /></Field>
          <Field label="Driver Name"><input value={form.driver_name} onChange={set('driver_name')} className="input" /></Field>
        </div>
        <Field label="Delivery Address (defaults to order site)"><input value={form.delivery_address} onChange={set('delivery_address')} className="input" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Plant"><input value={form.plant} onChange={set('plant')} className="input" placeholder="Plant-1" /></Field>
          <Field label="Batch No"><input value={form.batch_no} onChange={set('batch_no')} className="input" placeholder="Auto if blank" /></Field>
          <Field label="Slump (mm)"><input type="number" value={form.slump} onChange={set('slump')} className="input" /></Field>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <input id="pump" type="checkbox" checked={form.pump_used} onChange={set('pump_used')} />
          <label htmlFor="pump" className="text-sm text-slate-700">Pump used</label>
          {form.pump_used && <input value={form.pump_gang} onChange={set('pump_gang')} className="input ml-3 flex-1" placeholder="Pump gang" />}
        </div>
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <Field label="Rate override (₹/m³ — blank = order rate)"><input type="number" value={form.rate_override} onChange={set('rate_override')} className="input" placeholder={line ? String(line.rate) : ''} /></Field>
          <div className="flex items-end">
            {form.qty_m3 && effRate ? (
              <div className="text-xs text-slate-500 pb-2">
                {inrCompact(Number(form.qty_m3) * effRate)} + GST {gst}% = <span className="font-display font-bold text-slate-800">{inrCompact(Number(form.qty_m3) * effRate * (1 + gst / 100))}</span>
              </div>
            ) : null}
          </div>
        </div>
        <button onClick={save} disabled={saving || !line} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Raise Challan & Produce Batch'}
        </button>
      </div>
    </ModalShell>
  );
}

function ChallanDetailModal({ challan: initial, onClose, onUpdated }) {
  const [challan, setChallan] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [showDeliver, setShowDeliver] = useState(false);
  const [showDivert, setShowDivert] = useState(false);
  const [ewbNo, setEwbNo] = useState('');

  // Full detail (list rows exclude signature/photo for payload size)
  useEffect(() => {
    apiGet(`/dispatch/challans/${initial.id}`).then(setChallan).catch(() => {});
  }, [initial.id]);

  const cancel = async () => {
    setSaving(true);
    try {
      await apiPatch(`/dispatch/challans/${challan.id}/status`, { status: 'cancelled' });
      toast.success('Challan cancelled');
      onUpdated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const saveEwb = async () => {
    setSaving(true);
    try {
      await apiPatch(`/dispatch/challans/${challan.id}/ewb`, { ewb_no: ewbNo });
      toast.success('E-way bill recorded');
      setChallan({ ...challan, ewb_no: ewbNo.replace(/\s/g, '') });
      setEwbNo('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record EWB'); }
    finally { setSaving(false); }
  };

  const downloadEwbJson = async () => {
    try {
      const data = await apiGet(`/dispatch/challans/${challan.id}/ewb-json`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `EWB_${challan.challan_no.replace(/\//g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error('Failed to build EWB JSON'); }
  };

  const printTicket = () => {
    const w = window.open('', '_blank', 'width=720,height=900');
    if (!w) return toast.error('Pop-up blocked — allow pop-ups to print');
    const pw = challan.pour_window || {};
    w.document.write(`<html><head><title>${challan.challan_no}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
      h2{margin:0}.muted{color:#64748b;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:12px}
      td{padding:6px 8px;border:1px solid #e2e8f0;font-size:13px}
      td:first-child{color:#64748b;width:38%}
      img{max-height:90px;border:1px solid #e2e8f0;margin-top:4px}
    </style></head><body>
      <h2>ELR Industries Pvt Ltd — Delivery Ticket</h2>
      <div class="muted">Challan ${challan.challan_no} · ${new Date(challan.date).toLocaleString('en-IN')}</div>
      <table>
        <tr><td>Customer</td><td>${challan.party_name}${challan.jobsite ? ' — ' + challan.jobsite : ''}</td></tr>
        <tr><td>Grade / Quantity</td><td><b>${challan.grade}</b> · ${challan.qty_m3} m³ ${challan.slump ? '· slump ' + challan.slump + ' mm' : ''}</td></tr>
        <tr><td>Vehicle / Driver</td><td>${challan.vehicle_no} · ${challan.driver_name || '—'}</td></tr>
        <tr><td>Batch</td><td>${challan.batch_no || '—'} (dispatched ${new Date(challan.dispatch_time).toLocaleTimeString('en-IN')})</td></tr>
        ${challan.delivered_at ? `<tr><td>Delivered</td><td>${new Date(challan.delivered_at).toLocaleString('en-IN')} — ${challan.delivery_minutes_after_batch ?? '—'} min after batching${challan.pour_window_breached ? ' <b style="color:#b91c1c">(over pour window)</b>' : ''}</td></tr>` : ''}
        ${challan.pod_receiver_name ? `<tr><td>Received by</td><td>${challan.pod_receiver_name}${challan.pod_receiver_phone ? ' · ' + challan.pod_receiver_phone : ''}${challan.pod_signature ? '<br/><img src="' + challan.pod_signature + '"/>' : ''}</td></tr>` : ''}
        ${challan.water_added_l ? `<tr><td>Water added at site</td><td>${challan.water_added_l} L</td></tr>` : ''}
        ${challan.qty_rejected_m3 ? `<tr><td>Qty rejected</td><td>${challan.qty_rejected_m3} m³</td></tr>` : ''}
        ${challan.ewb_no ? `<tr><td>E-way bill</td><td>${challan.ewb_no}</td></tr>` : ''}
        <tr><td>Amount</td><td>₹${(challan.amount || 0).toLocaleString('en-IN')} + GST ${challan.gst_pct}% = <b>₹${(challan.total_amount || 0).toLocaleString('en-IN')}</b></td></tr>
      </table>
      <p class="muted" style="margin-top:20px">Time-since-batching window: ${pw.deadline ? new Date(pw.deadline).toLocaleTimeString('en-IN') + ' deadline' : '—'} · Generated by ELR Industries ERP</p>
      <script>window.print()</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <ModalShell title={challan.challan_no} onClose={onClose} wide>
      <div className="space-y-4">
        {challan.diversion_history?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 space-y-1">
            <div className="kpi-overline text-amber-700 flex items-center gap-1"><Route size={11} /> Load Diverted {challan.diversion_history.length > 1 ? `(${challan.diversion_history.length}×)` : ''}</div>
            {challan.diversion_history.map((h, i) => (
              <div key={i}>
                <b>{h.from_party_name}</b>{h.from_jobsite ? ` (${h.from_jobsite})` : ''} → <b>{h.to_party_name}</b>{h.to_jobsite ? ` (${h.to_jobsite})` : ''}
                {' — '}{h.reason} <span className="text-amber-600">· {formatISTDateTime(h.diverted_at)} by {h.diverted_by}</span>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-slate-400 text-xs">Party</div>{challan.party_name}</div>
          <div><div className="text-slate-400 text-xs">Jobsite</div>{challan.jobsite || '—'}</div>
          <div><div className="text-slate-400 text-xs">Grade</div><span className="font-display font-bold">{challan.grade}</span></div>
          <div><div className="text-slate-400 text-xs">Quantity</div>{challan.qty_m3} m³</div>
          <div><div className="text-slate-400 text-xs">Vehicle</div><span className="font-mono">{challan.vehicle_no}</span></div>
          <div><div className="text-slate-400 text-xs">Driver</div>{challan.driver_name || '—'}</div>
          <div><div className="text-slate-400 text-xs">Batch No</div>{challan.batch_no || '—'}</div>
          <div><div className="text-slate-400 text-xs">Slump</div>{challan.slump ? `${challan.slump} mm` : '—'}</div>
          <div><div className="text-slate-400 text-xs">Pump</div>{challan.pump_used ? `Yes — ${challan.pump_gang || ''}` : 'No'}</div>
          <div><div className="text-slate-400 text-xs">Status</div>
            <span className={`tag ${statusColor(challan.status)}`}>{challan.status}</span>{' '}
            <PourChip pw={challan.pour_window} />
          </div>
        </div>

        {challan.status === 'delivered' && challan.pod_receiver_name && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm space-y-1">
            <div className="kpi-overline">Delivery Ticket (ePOD)</div>
            <div>Received by <b>{challan.pod_receiver_name}</b>{challan.pod_receiver_phone ? ` · ${challan.pod_receiver_phone}` : ''} at {formatISTDateTime(challan.delivered_at)}</div>
            <div className="text-xs text-slate-600">
              {challan.delivery_minutes_after_batch != null && <>Delivered <b>{challan.delivery_minutes_after_batch} min</b> after batching{challan.pour_window_breached && <span className="text-red-600 font-semibold"> — over the pour window</span>}. </>}
              {challan.water_added_l ? `Water added at site: ${challan.water_added_l} L. ` : ''}
              {challan.qty_rejected_m3 ? `Rejected: ${challan.qty_rejected_m3} m³. ` : ''}
              {challan.pod_lat != null && `GPS ${challan.pod_lat.toFixed(4)}, ${challan.pod_lng.toFixed(4)}.`}
            </div>
            {challan.pod_signature && <img src={challan.pod_signature} alt="signature" className="h-14 bg-white border border-slate-200 rounded-sm" />}
            {challan.pod_photo && <img src={challan.pod_photo} alt="site" className="h-20 rounded-sm border border-slate-200 object-cover" />}
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Amount</span><span>{inrCompact(challan.amount)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">GST ({challan.gst_pct}%)</span><span>{inrCompact(challan.gst_amount)}</span></div>
          <div className="flex justify-between font-display font-bold text-base border-t border-slate-200 pt-1 mt-1"><span>Total</span><span>{inrCompact(challan.total_amount)}</span></div>
          <div className="flex justify-between text-xs text-slate-400 pt-1"><span>Material cost (internal)</span><span>{inrCompact(challan.material_cost)}</span></div>
        </div>

        {/* E-way bill */}
        {challan.status !== 'cancelled' && (
          <div className="border border-slate-200 rounded-sm p-3 text-sm space-y-2">
            <div className="kpi-overline">E-Way Bill</div>
            {challan.ewb_no ? (
              <div>EWB <span className="font-mono font-semibold">{challan.ewb_no}</span>{challan.ewb_valid_until ? ` · valid until ${challan.ewb_valid_until}` : ''}</div>
            ) : (
              <div className="flex gap-2">
                <input value={ewbNo} onChange={(e) => setEwbNo(e.target.value)} className="input flex-1" placeholder="12-digit EWB number from portal" />
                <button onClick={saveEwb} disabled={saving || !ewbNo.trim()} className="btn-secondary">Record</button>
              </div>
            )}
            <button onClick={downloadEwbJson} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1">
              <FileDown size={12} /> Download NIC bulk-generation JSON (EWB-01)
            </button>
          </div>
        )}

        {challan.shortages?.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-700">
            Stock shortage: {challan.shortages.join('; ')}
          </div>
        )}

        <div className="flex gap-2">
          {challan.status === 'dispatched' && (
            <>
              <button onClick={() => setShowDeliver(true)} disabled={saving} className="btn-primary flex-1 justify-center">
                <PenLine size={13} className="mr-1" /> Record Delivery
              </button>
              <button onClick={() => setShowDivert(true)} disabled={saving} className="btn-secondary flex-1 justify-center flex items-center gap-1">
                <Route size={13} /> Divert Load
              </button>
              <button onClick={cancel} disabled={saving} className="btn-secondary flex-1 justify-center">Cancel Challan</button>
            </>
          )}
          <button onClick={printTicket} className="btn-secondary justify-center flex items-center gap-1">
            <Printer size={13} /> Print Ticket
          </button>
        </div>
      </div>
      {showDeliver && (
        <DeliverModal challan={challan} onClose={() => setShowDeliver(false)}
          onDone={() => { setShowDeliver(false); onUpdated(); }} />
      )}
      {showDivert && (
        <DivertModal challan={challan} onClose={() => setShowDivert(false)}
          onDone={() => { setShowDivert(false); onUpdated(); }} />
      )}
    </ModalShell>
  );
}

function DivertModal({ challan, onClose, onDone }) {
  const [mode, setMode] = useState('order');   // 'order' = onto an open order line, 'adhoc' = free-text emergency
  const [lines, setLines] = useState([]);      // grade-matched open order lines
  const [lineKey, setLineKey] = useState('');  // "order_id::seq" of the picked target line
  const [form, setForm] = useState({
    party_name: '', jobsite: '', delivery_address: '', rate_per_m3: '', reason: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    apiGet('/commercial/orders/open-lines').then((r) => {
      // Only lines of the SAME grade as the loaded truck, and never the load's own current line.
      const matches = (r.items || []).filter((l) =>
        String(l.grade || '').toUpperCase() === String(challan.grade || '').toUpperCase()
        && !(l.order_id === challan.order_id && Number(l.seq) === Number(challan.order_item_seq)));
      setLines(matches);
      if (!matches.length) setMode('adhoc');   // nothing to divert onto → force ad-hoc
    }).catch(() => { setLines([]); setMode('adhoc'); });
  }, [challan.grade, challan.order_id, challan.order_item_seq]);

  const picked = lines.find((l) => `${l.order_id}::${l.seq}` === lineKey);

  const save = async () => {
    if (!form.reason.trim()) return toast.error('A reason is required for the audit trail');
    let payload = {
      jobsite: form.jobsite || null,
      delivery_address: form.delivery_address || null,
      rate_per_m3: form.rate_per_m3 ? Number(form.rate_per_m3) : null,
      reason: form.reason.trim(),
    };
    if (mode === 'order') {
      if (!picked) return toast.error('Pick the order to divert this load onto');
      payload = { ...payload, new_order_id: picked.order_id, new_order_item_seq: picked.seq };
    } else {
      if (!form.party_name.trim()) return toast.error('Name the new customer, or divert onto an existing order');
      payload = { ...payload, party_name: form.party_name.trim() };
    }
    setSaving(true);
    try {
      const res = await apiPatch(`/dispatch/challans/${challan.id}/divert`, payload);
      const onto = res.re_linked_order ? ` (order ${res.re_linked_order})` : '';
      toast.success(res.ewb_invalidated
        ? `Diverted to ${res.diverted_to}${onto} — previous e-way bill cleared, generate a new one`
        : `Diverted to ${res.diverted_to}${onto}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to divert load'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Divert Load — ${challan.challan_no}`} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-2">
          Currently en route to <b>{challan.party_name}</b>{challan.jobsite ? ` (${challan.jobsite})` : ''}. This truck's {challan.qty_m3} m³ of {challan.grade} can't be returned to the plant — redirect it to a different site instead. The original order gets its {challan.qty_m3} m³ back automatically.
        </div>

        {/* Where does the load go? */}
        <div className="flex gap-2">
          <button type="button" onClick={() => setMode('order')} disabled={!lines.length}
            className={`flex-1 px-3 py-2 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${mode === 'order' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-300'} disabled:opacity-40`}>
            Onto another order {lines.length ? `(${lines.length})` : ''}
          </button>
          <button type="button" onClick={() => setMode('adhoc')}
            className={`flex-1 px-3 py-2 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${mode === 'adhoc' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-300'}`}>
            Ad-hoc customer
          </button>
        </div>

        {mode === 'order' ? (
          <>
            <Field label={`Divert onto open ${challan.grade} order line *`}>
              <select value={lineKey} onChange={(e) => setLineKey(e.target.value)} className="input">
                <option value="">— pick an open {challan.grade} order —</option>
                {lines.map((l) => (
                  <option key={`${l.order_id}::${l.seq}`} value={`${l.order_id}::${l.seq}`}>
                    {l.order_no} · {l.party_name} · {l.pending_qty} m³ pending @ ₹{l.rate}
                  </option>
                ))}
              </select>
            </Field>
            {picked && (
              <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-sm p-2">
                Re-bills to <b>{picked.party_name}</b> at their contracted ₹{picked.rate}/m³.
                {challan.qty_m3 > picked.pending_qty && (
                  <span className="text-rose-600"> This load ({challan.qty_m3} m³) exceeds that line's {picked.pending_qty} m³ pending — it will be rejected.</span>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="New Customer / Party *"><input value={form.party_name} onChange={set('party_name')} className="input" autoFocus /></Field>
            <Field label="New Rate (₹/m³) — blank keeps current"><input type="number" value={form.rate_per_m3} onChange={set('rate_per_m3')} className="input" placeholder={challan.rate_per_m3} /></Field>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="New Jobsite"><input value={form.jobsite} onChange={set('jobsite')} className="input" /></Field>
          <Field label="New Delivery Address"><input value={form.delivery_address} onChange={set('delivery_address')} className="input" /></Field>
        </div>
        {mode === 'order' && (
          <Field label="Override Rate (₹/m³) — blank uses the order's rate"><input type="number" value={form.rate_per_m3} onChange={set('rate_per_m3')} className="input" placeholder={picked ? picked.rate : ''} /></Field>
        )}
        <Field label="Reason *"><input value={form.reason} onChange={set('reason')} className="input" placeholder="Original site cancelled — diverted to nearby job" /></Field>
        {challan.ewb_no && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
            This challan has a recorded e-way bill ({challan.ewb_no}) — diverting will clear it since GST law ties the EWB to the delivery destination. You'll need to record a new one.
          </div>
        )}
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-1">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <><Route size={13} className="mr-1" /> Confirm Diversion</>}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Weighbridge ──────────────────────────────────────────────────────────────
function WeighbridgeTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/weighbridge/entries').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load weighbridge entries')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Weighbridge Entries · {items.length}</div>
        <button onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="weighbridge-add-btn">
          <Plus size={13} /> Log Weighment
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Vehicle</th><th>Purpose</th><th className="text-right">Gross (kg)</th>
            <th className="text-right">Tare (kg)</th><th className="text-right">Net (kg)</th><th>Time</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No weighbridge entries yet.</td></tr>}
            {items.map((w) => (
              <tr key={w.id}>
                <td className="font-mono text-xs font-semibold">{w.vehicle_no}</td>
                <td className="capitalize">{w.purpose}</td>
                <td className="text-right tabular-nums">{w.gross_weight_kg}</td>
                <td className="text-right tabular-nums">{w.tare_weight_kg}</td>
                <td className="text-right tabular-nums font-display font-bold">{w.net_weight_kg}</td>
                <td className="text-xs">{formatISTDateTime(w.weighed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddWeighbridgeModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddWeighbridgeModal({ onClose, onSaved }) {
  const [vehicleNo, setVehicleNo] = useState('');
  const [gross, setGross] = useState('');
  const [tare, setTare] = useState('');
  const [purpose, setPurpose] = useState('outward');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!vehicleNo.trim()) return toast.error('Vehicle number is required');
    if (!gross || !tare) return toast.error('Enter gross and tare weight');
    if (Number(gross) <= Number(tare)) return toast.error('Gross weight must exceed tare weight');
    setSaving(true);
    try {
      await apiPost('/weighbridge/entries', {
        vehicle_no: vehicleNo.trim(), gross_weight_kg: Number(gross), tare_weight_kg: Number(tare), purpose,
      });
      toast.success('Weighment logged');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to log weighment'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Log Weighbridge Reading" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Vehicle No"><input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} className="input" placeholder="MH14EM8861" autoFocus /></Field>
        <Field label="Purpose">
          <select value={purpose} onChange={(e) => setPurpose(e.target.value)} className="input">
            <option value="outward">Outward (loaded truck leaving)</option>
            <option value="inward">Inward (raw material delivery)</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Gross Weight (kg)"><input type="number" value={gross} onChange={(e) => setGross(e.target.value)} className="input" /></Field>
          <Field label="Tare Weight (kg)"><input type="number" value={tare} onChange={(e) => setTare(e.target.value)} className="input" /></Field>
        </div>
        {gross && tare && Number(gross) > Number(tare) && (
          <div className="text-xs text-slate-500 text-right">Net: <span className="font-display font-bold text-slate-800">{Number(gross) - Number(tare)} kg</span></div>
        )}
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Log Weighment'}
        </button>
      </div>
    </ModalShell>
  );
}
